import { createPortal } from "react-dom";
import { useLayoutEffect, useEffect, useState, useRef } from "react";

export default function DropdownPortal({
  anchorRef,
  open,
  onClose,
  children,
  offset = 6,
  align = "left",
  className = "",
}) {
  const portalRef = useRef(null);
  const [style, setStyle] = useState({
    position: "fixed",
    visibility: "hidden",
    zIndex: 1000,
  });

  useLayoutEffect(() => {
    if (!open) return;
    const el = anchorRef?.current;
    if (!el) return;

    const updatePosition = () => {
      const r = el.getBoundingClientRect();
      const portalEl = portalRef.current;
      const portalWidth = portalEl ? portalEl.offsetWidth : 0;
      const viewportWidth = window.innerWidth;

      let left;
      if (align === "right") {
        left = r.right - (portalWidth || 360);
      } else {
        left = r.left;
        if (portalWidth > 0 && left + portalWidth > viewportWidth - 8) {
          left = Math.max(8, viewportWidth - portalWidth - 8);
        }
      }

      // Clamping seguro para nunca vazar das bordas da janela
      if (portalWidth > 0 && left + portalWidth > viewportWidth - 8) {
        left = viewportWidth - portalWidth - 8;
      }
      if (left < 8) {
        left = 8;
      }

      setStyle({
        position: "fixed",
        top: Math.round(r.bottom + offset),
        left: Math.round(left),
        minWidth: align === "left" ? Math.round(r.width) : undefined,
        maxWidth: "calc(100vw - 16px)",
        boxSizing: "border-box",
        visibility: "visible",
        zIndex: 1000,
      });
    };

    updatePosition();
  }, [open, anchorRef, offset, align]);

  useEffect(() => {
    if (!open) return;
    const el = anchorRef?.current;
    if (!el) return;

    const updatePosition = () => {
      const r = el.getBoundingClientRect();
      const portalEl = portalRef.current;
      const portalWidth = portalEl ? portalEl.offsetWidth : 0;
      const viewportWidth = window.innerWidth;

      let left;
      if (align === "right") {
        left = r.right - (portalWidth || 360);
      } else {
        left = r.left;
        if (portalWidth > 0 && left + portalWidth > viewportWidth - 8) {
          left = Math.max(8, viewportWidth - portalWidth - 8);
        }
      }

      if (portalWidth > 0 && left + portalWidth > viewportWidth - 8) {
        left = viewportWidth - portalWidth - 8;
      }
      if (left < 8) {
        left = 8;
      }

      setStyle((prev) => ({
        ...prev,
        top: Math.round(r.bottom + offset),
        left: Math.round(left),
        visibility: "visible",
      }));
    };

    const handleDocClick = (e) => {
      const clickedAnchor = el.contains(e.target);
      const clickedInsideAnyPortal = e.target.closest('[data-portal="dropdown"]');
      if (!clickedAnchor && !clickedInsideAnyPortal) onClose?.();
    };

    window.addEventListener("resize", updatePosition, { passive: true });
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("click", handleDocClick);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("click", handleDocClick);
    };
  }, [open, anchorRef, offset, align, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={portalRef}
      data-portal="dropdown"
      style={style}
      className={className || "bg-white border rounded-xl shadow-lg"}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}
